<template>
  <section class="ca-timeline">
    <div class="ca-timeline__header">
      <div class="ca-timeline__header-row ca-timeline__header-row--legend">
        <div class="ca-timeline-legend" :dir="isRTL ? 'rtl' : 'ltr'">
          <span class="ca-timeline-legend__title">{{ $t('ca_timeline_all_cleaners') }}</span>
          <span
            v-for="item in legendItems"
            :key="item.key"
            class="ca-timeline-legend__item"
          >
            <span
              v-if="item.warningIcon"
              class="ca-timeline-legend__warning"
              aria-hidden="true"
            >⚠</span>
            <span
              v-else-if="item.statusDot"
              class="ca-timeline-legend__dot"
              :class="`is-${item.key}`"
              aria-hidden="true"
            ></span>
            <span
              v-else-if="item.openPulse"
              class="ca-timeline-legend__dot is-still-open"
              aria-hidden="true"
            ></span>
            <img
              v-else-if="item.visitsIcon"
              :src="visitsIconUrl"
              alt="visits"
              class="ca-timeline-legend__visits"
            />
            <span
              v-else
              class="ca-timeline-legend__swatch"
              :class="`is-${item.key}`"
            ></span>
            {{ $t(item.label) }}
          </span>
        </div>
      </div>

      <div class="ca-timeline__header-row ca-timeline__header-row--labels">
        <div class="ca-timeline__cleaner-head ca-timeline__cleaner-label">{{ $t('cleaner') }}</div>
      </div>
    </div>

    <div
      v-for="row in cleanerRows"
      :key="row.cleaner.employeeId"
      :id="'ca-row-' + row.cleaner.employeeId"
      class="ca-row"
      :class="{ 'is-highlighted': highlightEmployeeId === row.cleaner.employeeId }"
    >
      <div class="ca-row__cleaner">
        <div class="ca-row__identity">
          <span
            class="ca-row__status-dot"
            :class="`is-${row.clockDotStatus}`"
            :title="clockDotTitle(row.clockDotStatus)"
          ></span>
          <span class="ca-row__name">
            {{ row.cleaner.username || '—' }}
          </span>
        </div>
        <div class="ca-row__meta">
          <div v-if="row.statChips.length" class="ca-row__stats">
            <span
              v-for="chip in row.statChips"
              :key="chip.key"
              class="ca-row__stat"
              :class="chip.className"
              :title="chip.title"
            >{{ chip.label }}</span>
          </div>
          <div class="ca-row__phone">{{ row.cleaner.phone }}</div>
          <div v-if="row.clockSessions.length" class="ca-row__clock-history">
            <div
              v-for="session in row.visibleClockSessions"
              :key="session.key"
              class="ca-row__clock-session"
              :title="session.title"
            >
              <span>{{ session.inLabel }}</span>
              <span class="ca-row__clock-sep">→</span>
              <span :class="{ 'is-open': session.isOpen }">
                {{ session.isOpen ? '…' : session.outLabel }}
              </span>
            </div>
            <button
              v-if="row.clockSessionsHidden"
              type="button"
              class="ca-row__clock-toggle"
              @click.stop="toggleClockHistory(row.cleaner.employeeId)"
            >
              +{{ row.clockSessionsHidden }} {{ $t('ca_clock_sessions_more') }}
            </button>
            <button
              v-else-if="isClockHistoryExpanded(row.cleaner.employeeId) && row.clockSessions.length > CLOCK_SESSIONS_PREVIEW"
              type="button"
              class="ca-row__clock-toggle"
              @click.stop="toggleClockHistory(row.cleaner.employeeId)"
            >
              {{ $t('ca_clock_sessions_less') }}
            </button>
          </div>
        </div>
      </div>

      <div class="ca-row__track" :dir="isRTL ? 'rtl' : 'ltr'">
        <span v-if="row.isEmpty" class="ca-row__empty">{{ $t('no_activity_today') }}</span>

        <template v-for="item in row.trackItems">
          <div
            v-if="item.type === 'transit'"
            :key="item.key"
            class="ca-transit"
            :class="{ 'is-live': item.isLive, 'is-visits-only': !item.hasTransit }"
            :title="transitTitle(item)"
            :aria-label="transitTitle(item)"
          >
            <template v-if="item.hasTransit">
              <span v-if="item.isLive" class="ca-transit__live-dot" aria-hidden="true"></span>
              <span v-else class="ca-transit__icon" :class="{ 'is-rtl': isRTL }" aria-hidden="true">→</span>
              <span class="ca-transit__time">{{ transitLabel(item) }}</span>
            </template>
            <span v-if="item.visits != null" class="ca-transit__visits">
              <img :src="visitsIconUrl" alt="visits" class="ca-transit__visits-icon" />{{ item.visits }}
            </span>
          </div>
          <div
            v-else
            :key="item.key"
            class="ca-complaint-box"
            :class="{
              'is-work': true,
              'is-new': item.box.isNew,
              'has-sla-breach': item.box.isSlaBreach,
              'is-still-open': item.box.isOpen,
              'is-deferred': item.box.isDeferred,
            }"
            :aria-label="item.box.ariaLabel"
          >
            <span v-if="item.box.isOpen" class="ca-complaint-box__open-dot" aria-hidden="true"></span>
            <span
              v-if="item.box.isSlaBreach"
              class="ca-complaint-box__breach-icon"
              :title="$t('sla_breached')"
              :aria-label="$t('sla_breached')"
            >⚠</span>
            <CleanerActivityDeferredTooltip
              v-if="item.box.isDeferred"
              class="ca-complaint-box__deferred-badge"
              :room-name="item.box.roomName"
              :is-open="item.box.isOpen"
              :rooms="item.box.deferredRooms"
              :aria-label="$t('ca_deferred_tip_title')"
            >↪</CleanerActivityDeferredTooltip>
            <span class="ca-complaint-box__room">{{ item.box.roomName }}</span>
            <span class="ca-complaint-box__time">{{ item.box.timeLabel }}</span>
            <div v-if="item.box.servikIcons.length" class="ca-complaint-box__icons">
              <CleanerActivityServikTooltip
                v-for="icon in item.box.servikIcons"
                :key="icon.key"
                :label="icon.servikLabel"
              >
                <span
                  v-if="icon.servikId === 39"
                  class="ca-complaint-box__icon is-heart"
                  :aria-label="$t(icon.servikLabel)"
                >💚</span>
                <img
                  v-else-if="icon.servikIconUrl"
                  :src="icon.servikIconUrl"
                  :style="icon.servikIconStyle"
                  :alt="$t(icon.servikLabel)"
                  class="ca-complaint-box__icon"
                />
                <span
                  v-else
                  class="ca-complaint-box__icon is-fallback"
                  :style="icon.servikIconStyle"
                  :aria-label="$t(icon.servikLabel)"
                ></span>
              </CleanerActivityServikTooltip>
            </div>
            <span v-if="item.box.durationLabel" class="ca-complaint-box__duration">{{ item.box.durationLabel }}</span>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import cleanerActivityMixin from './cleanerActivityMixin'
import CleanerActivityServikTooltip from './CleanerActivityServikTooltip.vue'
import CleanerActivityDeferredTooltip from './CleanerActivityDeferredTooltip.vue'
import { formatMinutes, formatTransitMs } from './cleanerActivityUtils'

const NEW_EVENT_HIGHLIGHT_MS = 6000
const CLOCK_SESSIONS_PREVIEW = 2
// Same visits icon the rest of the performance panel uses (hour table, KPI bars).
const VISITS_ICON_URL = require('@/assets/performance/icons-visits.svg')

export default {
  components: { CleanerActivityServikTooltip, CleanerActivityDeferredTooltip },
  mixins: [cleanerActivityMixin],
  props: {
    cleaners: { type: Array, required: true },
    rangeStart: { type: Date, required: true },
    rangeEnd: { type: Date, required: true },
    /** Room ids to scope timeline SLA bars (null/empty = no extra scope). */
    filterRoomIds: { type: Array, default: null },
    isLiveMode: { type: Boolean, default: false },
    highlightEmployeeId: { type: Number, default: null },
  },
  data() {
    return {
      recentKeys: {},
      expandedClockHistory: {},
      CLOCK_SESSIONS_PREVIEW,
      // Drives live "idle since last task" transit timers before still-open complaints.
      nowTick: Date.now(),
    }
  },
  created() {
    this._seenKeys = new Set()
    this._cleanerSig = null
    this._highlightTimers = {}
    this._liveTimer = null
  },
  mounted() {
    this.detectNewEvents()
    this.updateLiveTimer()
  },
  beforeDestroy() {
    Object.values(this._highlightTimers).forEach((timer) => clearTimeout(timer))
    if (this._liveTimer) clearInterval(this._liveTimer)
  },
  watch: {
    cleaners() {
      this.$nextTick(() => this.detectNewEvents())
    },
    isLiveMode() {
      this.updateLiveTimer()
    },
  },
  computed: {
    ...mapGetters(['isRTL']),
    visitsIconUrl() {
      return VISITS_ICON_URL
    },
    legendItems() {
      return [
        { key: 'work', label: 'ca_legend_work' },
        { key: 'still-open', label: 'complaint_still_open', openPulse: true },
        { key: 'late', label: 'clocked_in_late', statusDot: true },
        { key: 'breach', label: 'sla_breached', warningIcon: true },
        { key: 'visits', label: 'ca_legend_visits', visitsIcon: true },
      ]
    },
    rangeStartMs() {
      return this.toTimestamp(this.rangeStart)
    },
    rangeEndMs() {
      return this.toTimestamp(this.rangeEnd)
    },
    cleanerRows() {
      return this.cleaners.map((cleaner) => {
        try {
          const boxes = this.buildComplaintBoxes(cleaner)
          this.markDeferredBoxes(boxes, cleaner)
          const clockDotStatus = this.getCleanerClockDotStatus(cleaner)
          const clockSessions = this.buildCleanerClockHistory(cleaner)
          const { visibleClockSessions, clockSessionsHidden } = this.resolveClockSessionsDisplay(
            clockSessions,
            cleaner.employeeId,
          )
          return {
            cleaner,
            boxes,
            trackItems: this.buildTrackItems(boxes),
            clockDotStatus,
            clockSessions,
            visibleClockSessions,
            clockSessionsHidden,
            isEmpty: boxes.length === 0,
            statChips: this.buildStatChips(cleaner),
          }
        } catch (error) {
          console.error('[CleanerActivity] failed to build timeline row', cleaner.employeeId, error)
          return {
            cleaner,
            boxes: [],
            trackItems: [],
            clockDotStatus: 'out',
            clockSessions: [],
            visibleClockSessions: [],
            clockSessionsHidden: 0,
            isEmpty: true,
            statChips: [],
          }
        }
      })
    },
  },
  methods: {
    updateLiveTimer() {
      if (this._liveTimer) {
        clearInterval(this._liveTimer)
        this._liveTimer = null
      }
      if (this.isLiveMode) {
        this.nowTick = Date.now()
        this._liveTimer = setInterval(() => { this.nowTick = Date.now() }, 1000)
      }
    },
    /** Flag open-complaint boxes the cleaner deferred (worked other rooms while it stayed open). */
    markDeferredBoxes(boxes, cleaner) {
      const deferred = this.deferredComplaints(cleaner, this.effectiveNowMs())
      if (!deferred.length) return
      const bySlaId = new Map(deferred.map((entry) => [entry.complaint.slaId, entry]))
      boxes.forEach((box) => {
        const complaint = box.event && box.event.complaint
        const entry = complaint ? bySlaId.get(complaint.slaId) : null
        if (!entry) return
        box.isDeferred = true
        box.deferredRooms = entry.roomNames
      })
    },
    buildStatChips(cleaner) {
      const metrics = cleaner.metrics || {}
      const chips = []

      if (metrics.avgResponseMinutes != null) {
        chips.push({
          key: 'avg',
          label: `${formatMinutes(metrics.avgResponseMinutes, { compact: true })} ${this.$t('ca_stat_avg_short')}`,
          title: this.$t('ca_assigned_room_avg_hint'),
          className: 'is-neutral',
        })
      }

      if (metrics.openComplaints > 0) {
        chips.push({
          key: 'open',
          label: `${metrics.openComplaints} ${this.$t('ca_stat_open_short')}`,
          title: this.$t('open_complaints'),
          className: 'is-warning',
        })
      }

      if (metrics.slaBreaches > 0) {
        chips.push({
          key: 'breach',
          label: `${metrics.slaBreaches} ${this.$t('ca_stat_breach_short')}`,
          title: this.$t('sla_breached'),
          className: 'is-danger',
        })
      }

      if (metrics.totalWorkMinutes > 0) {
        chips.push({
          key: 'work',
          label: `${formatMinutes(metrics.totalWorkMinutes, { compact: true })} ${this.$t('ca_stat_work_short')}`,
          title: this.$t('total_work_time_net'),
          className: 'is-neutral',
        })
      }

      return chips
    },
    complaintInRange(complaint) {
      const startMs = this.toTimestamp(complaint.openTime)
      const endMs = complaint.closeTime ? this.toTimestamp(complaint.closeTime) : this.effectiveNowMs()
      if (Number.isNaN(startMs)) return false
      if (endMs < this.rangeStartMs || startMs > this.rangeEndMs) return false
      return true
    },
    complaintInRoomScope(roomId) {
      if (!this.filterRoomIds || !this.filterRoomIds.length) return true
      return this.filterRoomIds.includes(Number(roomId))
    },
    isClockHistoryExpanded(employeeId) {
      return !!this.expandedClockHistory[employeeId]
    },
    toggleClockHistory(employeeId) {
      this.$set(this.expandedClockHistory, employeeId, !this.expandedClockHistory[employeeId])
    },
    resolveClockSessionsDisplay(sessions, employeeId) {
      if (!sessions.length) {
        return { visibleClockSessions: [], clockSessionsHidden: 0 }
      }
      if (this.isClockHistoryExpanded(employeeId) || sessions.length <= CLOCK_SESSIONS_PREVIEW) {
        return { visibleClockSessions: sessions, clockSessionsHidden: 0 }
      }
      return {
        visibleClockSessions: sessions.slice(-CLOCK_SESSIONS_PREVIEW),
        clockSessionsHidden: sessions.length - CLOCK_SESSIONS_PREVIEW,
      }
    },
    buildComplaintTimeLabel(complaint) {
      const open = this.formatHM(complaint.openTime)
      if (!complaint.closeTime) return `${open} –`
      return `${open} – ${this.formatHM(complaint.closeTime)}`
    },
    buildComplaintBoxes(cleaner) {
      return (cleaner.complaints || [])
        .filter((complaint) => complaint.openTime)
        .filter((complaint) => this.complaintInRange(complaint))
        .filter((complaint) => this.complaintInRoomScope(complaint.roomId))
        .map((complaint, index) => {
          const roomName = this.roomName(complaint.roomId)
          const timeLabel = this.buildComplaintTimeLabel(complaint)
          const durationMs = this.shouldShowComplaintDuration(complaint)
            ? this.complaintDurationMs(complaint)
            : null
          const durationLabel = durationMs != null ? this.formatDuration(durationMs) : null
          const isOpen = !complaint.closeTime
          const isSlaBreach = this.isComplaintBreach(complaint)
          const servikIcons = this.buildServikIcons(complaint)
          const stableKey = `${cleaner.employeeId}|complaint|${complaint.slaId}`
          const event = { time: complaint.openTime, type: 'complaint', complaint, servikInfo: complaint.servikInfo }
          const visits = Number.isFinite(Number(complaint.visitsSincePrevSla))
            ? Number(complaint.visitsSincePrevSla)
            : null
          const ariaParts = [roomName, timeLabel]
          if (durationLabel) ariaParts.push(durationLabel)
          if (isSlaBreach) ariaParts.push(this.$t('sla_breached'))

          return {
            key: `complaint-${complaint.slaId}-${index}`,
            stableKey,
            event,
            roomName,
            timeLabel,
            isOpen,
            durationLabel,
            isSlaBreach,
            servikIcons,
            visits,
            visitsWindowStart: complaint.visitsWindowStart || null,
            isNew: !!this.recentKeys[stableKey],
            ariaLabel: ariaParts.join('. '),
          }
        })
        .sort((a, b) => this.toTimestamp(a.event.time) - this.toTimestamp(b.event.time))
    },
    /**
     * Interleave boxes (kept in open-time order) with gap chips carrying the idle time since the
     * previous task closed and how many people used the upcoming task's room since it was last
     * resolved. A chip is emitted whenever either half is known, so the row's first box can still
     * show its visit count even though nothing precedes it.
     * @param {Array<Object>} boxes
     * @returns {Array<{type:'box'|'transit', key:String, box?:Object}>}
     */
    buildTrackItems(boxes) {
      const items = []
      boxes.forEach((box, index) => {
        const gap = this.buildGapItem(index > 0 ? boxes[index - 1] : null, box)
        if (gap) items.push(gap)
        items.push({ type: 'box', key: box.key, box })
      })
      return items
    },
    /**
     * Chip shown before a box, combining the transit gap (when a previous task exists and closed)
     * with the box's own visit count. Live gaps keep only their start so the per-second label is
     * computed in the template (transitLabel) — this keeps cleanerRows independent of nowTick.
     * @returns {Object|null} null when neither a transit gap nor a visit count is available.
     */
    buildGapItem(prevBox, nextBox) {
      const transit = prevBox ? this.buildTransitTiming(prevBox, nextBox) : null
      if (!transit && nextBox.visits == null) return null
      return {
        type: 'transit',
        key: `gap-${nextBox.key}`,
        hasTransit: !!transit,
        isLive: !!transit && transit.isLive,
        startMs: transit ? transit.startMs : null,
        label: transit ? transit.label : null,
        visits: nextBox.visits,
        visitsRoomName: nextBox.roomName,
        visitsWindowStart: nextBox.visitsWindowStart,
      }
    },
    /**
     * Idle gap between the previous complaint's close and the next complaint. For a closed next
     * complaint this is the completed gap (prevClose -> nextOpen). For a still-open next complaint
     * it is a live timer (prevClose -> now) showing how long it has been since the previous task
     * closed. Non-positive gaps (back-to-back or overlapping tasks) are clamped to 0m.
     * @returns {{isLive:Boolean, startMs:Number, label:String|null}|null} null when the previous
     * complaint is still open or times are invalid.
     */
    buildTransitTiming(prevBox, nextBox) {
      const prevComplaint = prevBox.event && prevBox.event.complaint
      const nextComplaint = nextBox.event && nextBox.event.complaint
      if (!prevComplaint || !nextComplaint || !prevComplaint.closeTime) return null
      const closeMs = this.toTimestamp(prevComplaint.closeTime)
      if (Number.isNaN(closeMs)) return null

      if (!nextComplaint.closeTime && this.isLiveMode) {
        return { isLive: true, startMs: closeMs, label: null }
      }

      const endMs = this.toTimestamp(nextComplaint.openTime)
      if (Number.isNaN(endMs)) return null
      return { isLive: false, startMs: closeMs, label: formatTransitMs(Math.max(0, endMs - closeMs)) }
    },
    /** Transit label; live gaps tick from startMs using nowTick, closed gaps are static. */
    transitLabel(item) {
      if (!item.isLive) return item.label
      return formatTransitMs(Math.max(0, this.nowTick - item.startMs))
    },
    transitTitle(item) {
      const parts = []
      if (item.hasTransit) {
        const label = item.isLive ? this.$t('ca_transit_live_tooltip') : this.$t('ca_transit_tooltip')
        parts.push(`${label}: ${this.transitLabel(item)}`)
      }
      if (item.visits != null) {
        let visitsTitle = `${this.$t('ca_visits_between_sla')} (${item.visitsRoomName}): ${item.visits}`
        if (item.visitsWindowStart) {
          visitsTitle += ` — ${this.$t('ca_visits_since')} ${this.formatTime(item.visitsWindowStart)}`
        }
        parts.push(visitsTitle)
      }
      return parts.join('\n')
    },
    detectNewEvents() {
      const currentKeys = []
      this.cleanerRows.forEach((row) => {
        row.boxes.forEach((box) => currentKeys.push(box.stableKey))
      })

      const signature = this.cleaners.map((cleaner) => cleaner.employeeId).sort().join(',')
      if (this._cleanerSig !== signature) {
        this._cleanerSig = signature
        this._seenKeys = new Set(currentKeys)
        this.recentKeys = {}
        return
      }

      currentKeys.forEach((key) => {
        if (this._seenKeys.has(key)) return
        this._seenKeys.add(key)
        this.$set(this.recentKeys, key, true)
        if (this._highlightTimers[key]) clearTimeout(this._highlightTimers[key])
        this._highlightTimers[key] = setTimeout(() => {
          this.$delete(this.recentKeys, key)
          delete this._highlightTimers[key]
        }, NEW_EVENT_HIGHLIGHT_MS)
      })
    },
    scrollToCleaner(employeeId) {
      const element = document.getElementById(`ca-row-${employeeId}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/styles/mixins" as *;

$cleaner-col-width: 240px;

.ca-timeline {
  width: 100%;
}

.ca-timeline__header {
  margin-bottom: 6px;
}

.ca-timeline__header-row {
  display: flex;
  align-items: stretch;

  &--legend {
    margin-bottom: 4px;
  }

  &--labels {
    align-items: flex-end;
    min-height: 28px;
  }
}

.ca-timeline-legend {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 16px;
  width: 100%;
  padding-inline: 12px;
  padding-bottom: 4px;
  font-size: 0.72rem;
  color: #5a6573;
  text-align: start;

  &__title {
    font-size: 0.8rem;
    font-weight: 700;
    color: #2c3e50;
    white-space: nowrap;
    flex-shrink: 0;
    margin-inline-end: 8px;
  }

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 600;
    white-space: nowrap;
  }

  &__swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.08);

    &.is-work {
      background-color: $ca-timeline-work;
    }
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;

    &.is-late {
      background-color: $ca-timeline-late;
      box-shadow: 0 0 0 2px rgba(230, 126, 34, 0.2);
    }

    &.is-still-open {
      background-color: #e67e22;
      box-shadow: 0 0 0 2px #fff;
      animation: ca-open-dot-pulse 2.2s ease-in-out infinite;
    }
  }

  &__warning {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: $ca-breach;
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
    box-shadow: 0 0 0 1px #fff;
  }

  &__visits {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    object-fit: contain;
  }
}

.ca-timeline__cleaner-head {
  width: $cleaner-col-width;
  flex-shrink: 0;
  padding-inline-start: 12px;
}

.ca-timeline__cleaner-label {
  font-weight: $font-weight-semi-bold;
  color: #7f8c8d;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ca-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #edeff2;
  min-height: 64px;

  &:nth-child(even) {
    background-color: #fafbfc;
  }

  &:hover {
    background-color: #f2f6fb;
  }

  &.is-highlighted {
    background-color: #eaf3ff;
    box-shadow: inset 3px 0 0 #2f80eb;
  }

  &__cleaner {
    position: sticky;
    inset-inline-start: 0;
    z-index: 3;
    width: $cleaner-col-width;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 8px 12px;
    border-inline-end: 1px solid #eef0f3;
    background: inherit;
  }

  &__identity {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    margin-bottom: 2px;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding-inline-start: 15px;
    max-width: 100%;
  }

  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    max-width: 100%;
  }

  &__stat {
    display: inline-flex;
    align-items: center;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
    background: #eef2f7;
    color: #5a6573;

    &.is-warning {
      background: rgba(230, 126, 34, 0.12);
      color: #c67c2a;
    }

    &.is-danger {
      background: rgba(192, 57, 43, 0.12);
      color: #c0392b;
    }

    &.is-neutral {
      background: #eef2f7;
      color: #5a6573;
    }
  }

  &__clock-history {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 100%;
  }

  &__clock-session {
    font-size: 0.68rem;
    font-weight: 500;
    color: #7f8c8d;
    line-height: 1.2;
    white-space: nowrap;

    .is-open {
      color: #5f9b46;
      font-weight: 600;
    }
  }

  &__clock-sep {
    margin-inline: 2px;
    opacity: 0.75;
  }

  &__clock-toggle {
    padding: 0;
    border: none;
    background: none;
    font-size: 0.66rem;
    font-weight: 600;
    color: #2f80eb;
    cursor: pointer;
    line-height: 1.2;
    text-align: start;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid rgba(47, 128, 235, 0.45);
      outline-offset: 1px;
      border-radius: 2px;
    }
  }

  &__status-dot {
    flex-shrink: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;

    &.is-in {
      background-color: #5f9b46;
      box-shadow: 0 0 0 3px rgba(95, 155, 70, 0.2);
    }

    &.is-out {
      background-color: #d4351c;
      box-shadow: 0 0 0 3px rgba(212, 53, 28, 0.15);
    }

    &.is-late {
      background-color: $ca-timeline-late;
      box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.2);
    }
  }

  &__name {
    font-weight: 600;
    font-size: 0.9rem;
    white-space: nowrap;
    color: #2c3e50;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  &__phone {
    font-size: 0.78rem;
    color: #7f8c8d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  &__track {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    min-height: 52px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  &__empty {
    color: #b8c0c9;
    font-size: 0.78rem;
    font-style: italic;
    white-space: nowrap;
  }
}

.ca-complaint-box {
  position: relative;
  flex-shrink: 0;
  width: max-content;
  min-height: 52px;
  border-radius: 6px;
  border: 1px solid transparent;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  color: #fff;
  text-align: start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  &__breach-icon {
    position: absolute;
    top: -5px;
    inset-inline-end: -5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: $ca-breach;
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0 0 2px #fff;
    pointer-events: none;
    z-index: 1;
  }

  &__icons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    max-width: 100%;
  }

  &__icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    padding: 2px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    box-sizing: border-box;
    object-fit: contain;
    cursor: default;

    &.is-heart {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      font-size: 0.7rem;
      line-height: 1;
      background-color: #5f9b46;
      border-color: rgba(255, 255, 255, 0.85);
    }

    &.is-fallback {
      display: block;
      padding: 0;
    }
  }

  &__room {
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    max-width: 100%;
  }

  &__time {
    font-size: 0.72rem;
    font-weight: 500;
    line-height: 1.15;
    color: rgba(255, 255, 255, 0.9);
    white-space: nowrap;
  }

  &__open-dot {
    position: absolute;
    bottom: 6px;
    inset-inline-end: 6px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    box-sizing: border-box;
    background-color: #e67e22;
    box-shadow: 0 0 0 2px #fff;
    animation: ca-open-dot-pulse 2.2s ease-in-out infinite;
    z-index: 1;
    pointer-events: none;
  }

  &__duration {
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.1;
    color: #fff;
    white-space: nowrap;
  }

  &.is-work {
    background-color: $ca-timeline-work;
    border-color: $ca-timeline-work;
  }

  &.is-still-open {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.75);
  }

  &__deferred-badge {
    position: absolute;
    bottom: -5px;
    inset-inline-start: -5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #e67e22;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0 0 2px #fff;
    cursor: default;
    z-index: 1;
  }

  &.is-deferred {
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.12),
      0 0 0 2px #e67e22;
  }

  &.is-new {
    animation: ca-new-flash 1.2s ease-in-out 0s 3;
  }
}

.ca-transit {
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f0f2f5;
  border: 1px dashed #d4dae1;
  color: #8a96a3;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;

  &__icon {
    display: inline-block;
    font-size: 0.72rem;
    line-height: 1;

    &.is-rtl {
      transform: scaleX(-1);
    }
  }

  &__time {
    line-height: 1;
  }

  &__visits {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    line-height: 1;
    color: color.adjust($bouquet, $lightness: -18%);

    // Separator from the transit time; absent when the chip carries visits only.
    &:not(:first-child) {
      padding-inline-start: 6px;
      border-inline-start: 1px solid #d4dae1;
    }
  }

  &__visits-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &.is-live {
    background: rgba(230, 126, 34, 0.1);
    border-color: rgba(230, 126, 34, 0.45);
    color: #c67c2a;
  }

  &.is-visits-only {
    background: rgba($bouquet, 0.1);
    border-color: rgba($bouquet, 0.4);
  }

  &__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #e67e22;
    flex-shrink: 0;
    animation: ca-open-dot-pulse 2.2s ease-in-out infinite;
  }
}

@keyframes ca-new-flash {
  0% { box-shadow: 0 0 0 0 rgba(47, 128, 235, 0.55); }
  50% { box-shadow: 0 0 0 6px rgba(47, 128, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(47, 128, 235, 0); }
}

@keyframes ca-open-dot-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 0 rgba(230, 126, 34, 0.5);
  }
  50% {
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px rgba(230, 126, 34, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ca-complaint-box__open-dot,
  .ca-timeline-legend__dot.is-still-open {
    animation: none;
  }
}

@media (max-width: 991px) {
  .ca-timeline__cleaner-head,
  .ca-row__cleaner {
    width: 165px;
  }
}

@media (max-width: 767px) {
  .ca-timeline__header-row--labels {
    display: none;
  }

  .ca-timeline-legend {
    flex-wrap: wrap;
    gap: 8px 12px;
    padding-inline: 4px;
    font-size: 0.68rem;

    &__title {
      flex: 1 1 100%;
      margin-inline-end: 0;
      margin-bottom: 2px;
      font-size: 0.76rem;
    }

    &__item {
      flex: 0 1 auto;
    }
  }

  .ca-row {
    flex-direction: column;
    min-height: 0;

    &.is-highlighted {
      box-shadow: inset 0 3px 0 #2f80eb;
    }
  }

  .ca-row__cleaner {
    position: static;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 4px 8px;
    padding: 10px 8px;
    border-inline-end: none;
    border-bottom: 1px solid #eef0f3;
  }

  .ca-row__identity {
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 0;
  }

  .ca-row__meta {
    flex: 1 1 100%;
    padding-inline-start: 15px;
  }

  .ca-row__name {
    font-size: 0.88rem;
  }

  .ca-row__phone {
    font-size: 0.74rem;
  }

  .ca-row__track {
    width: 100%;
    padding: 8px;
    min-height: 48px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .ca-complaint-box {
    min-height: 48px;
    padding: 5px 8px;

    &__room {
      font-size: 0.74rem;
    }

    &__time,
    &__duration {
      font-size: 0.68rem;
    }
  }

  .ca-transit {
    font-size: 0.64rem;
    padding: 2px 6px;
  }
}
</style>
